import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {

  CurrencyDollarIcon,
  ClockIcon,

  MapPinIcon,
  PhoneIcon,
  EnvelopeIcon,

  ArrowUturnLeftIcon,
  CheckCircleIcon,
  XCircleIcon,
  CalculatorIcon,
  TagIcon,
  BellAlertIcon,
  ShoppingBagIcon,

  UserIcon,
 
  ShoppingCartIcon,
  EyeIcon,
  ArrowPathIcon,
  ExclamationTriangleIcon,
  ShieldCheckIcon,
  RectangleStackIcon,
} from '@heroicons/react/24/outline';
import { fetchProductPartnerProfile, fetchVendorOrders } from '../../../services/MasterAdmin/productPartnerService';
import { ProductPartner } from '../../../models/MasterAdmin/productPartner';
import { toggleVendorStatus } from '../../../services/MasterAdmin/vendorService';
import FilePreview from '../../../components/MasterAdmin/FilePreview';
import { ProductOrder } from '../../../models/MasterAdmin/productOrder';

interface MetricCardProps {
  title: string;
  value: string | number;
  icon: React.ReactNode;
  trend?: string;
  trendValue?: string;
  description?: string;
}

const MetricCard: React.FC<MetricCardProps> = ({ title, value, icon, trend, trendValue, description }) => (
  <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-4 sm:p-6 hover:shadow-md transition-all duration-300 border border-gray-100 dark:border-gray-700">
    <div className="flex items-center justify-between">
      <div>
        <p className="text-sm font-medium text-gray-600 dark:text-gray-400">{title}</p>
        <p className="text-2xl font-semibold text-gray-900 dark:text-white mt-2">{value}</p>
        {trend && (
          <p className={`text-sm mt-2 ${trend === 'up' ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
            {trendValue}
          </p>
        )}
        {description && (
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">{description}</p>
        )}
      </div>
      <div className="p-3 bg-blue-50 dark:bg-blue-900/30 rounded-lg">{icon}</div>
    </div>
  </div>
);



interface OrderCounts {
  pending: number;
  completed: number;
  cancelled: number;
}

interface OrderListProps {
  orders: ProductOrder[];
  onSearch: (query: string) => void;
  onFilter: (status: string) => void;
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
}

const OrderList: React.FC<OrderListProps> = ({
  orders,
  onSearch,
  onFilter,
  currentPage,
  totalPages,
  onPageChange,
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    const query = e.target.value;
    setSearchQuery(query);
    onSearch(query);
  };

  const handleFilter = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const status = e.target.value;
    setStatusFilter(status);
    onFilter(status);
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-6 border border-gray-100 dark:border-gray-700">
      <div className="flex flex-col md:flex-row justify-between items-center mb-6 gap-4">
        <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Recent Orders</h2>
        <div className="flex flex-col md:flex-row gap-4 w-full md:w-auto">
          <input
            type="text"
            placeholder="Search orders..."
            value={searchQuery}
            onChange={handleSearch}
            className="px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
          />
          <select
            value={statusFilter}
            onChange={handleFilter}
            className="px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
          >
            <option value="all">All Status</option>
            <option value="pending">Pending</option>
            <option value="out_for_delivery">Out for Delivery</option>
            <option value="delivered">Delivered</option>
            <option value="cancelled">Cancelled</option>
          </select>
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
          <thead className="bg-gray-50 dark:bg-gray-700/50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Order ID</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Customer</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Products</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Amount</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Status</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Date</th>
            </tr>
          </thead>
          <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
            {orders.map((order) => (
              <tr key={order.orderId}>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">{order.orderId}</td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex items-center">
                    <div className="flex-shrink-0 h-10 w-10">
                      <div className="h-10 w-10 rounded-full bg-gray-100 dark:bg-gray-700 flex items-center justify-center">
                        <UserIcon className="h-6 w-6 text-gray-500 dark:text-gray-400" />
                      </div>
                    </div>
                    <div className="ml-4">
                      <div className="text-sm font-medium text-gray-900 dark:text-white">{order.userDetails.name}</div>
                      <div className="text-sm text-gray-500 dark:text-gray-400">{order.userDetails.emailId}</div>
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm text-gray-900 dark:text-white">
                    {order.items.map(item => `${item.productDetails.name} (${item.quantity})`).join(', ')}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">₹{order.totalAmount}</td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                    order.status === 'delivered'
                      ? 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300'
                      : order.status === 'pending'
                      ? 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-300'
                      : order.status === 'out_for_delivery'
                      ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300'
                      : 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300'
                  }`}>
                    {order.status.replace(/_/g, ' ').charAt(0).toUpperCase() + order.status.slice(1)}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                  {new Date(order.placedAt).toLocaleDateString()}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div className="flex justify-between items-center mt-6">
        <div className="text-sm text-gray-700 dark:text-gray-400">
          Showing page {currentPage} of {totalPages}
        </div>
        <div className="flex space-x-2">
          <button
            onClick={() => onPageChange(currentPage - 1)}
            disabled={currentPage === 1}
            className="px-3 py-1 rounded-md bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 disabled:opacity-50"
          >
            Previous
          </button>
          <button
            onClick={() => onPageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
            className="px-3 py-1 rounded-md bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 disabled:opacity-50"
          >
            Next
          </button>
        </div>
      </div>
    </div>
  );
};

const ProductVendorDashboard: React.FC = () => {
  const { vendorId } = useParams<{ vendorId: string }>();
  const navigate = useNavigate();
  const [productPartnerData, setProductPartnerData] = useState<ProductPartner | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showDetails, setShowDetails] = useState(false);
  const [updatingStatus, setUpdatingStatus] = useState<string | null>(null);
  const [,setServiceStatus] = useState(true);
  const [orders, setOrders] = useState<ProductOrder[]>([]);
  const [filteredOrders, setFilteredOrders] = useState<ProductOrder[]>([]);
  const [orderCounts, setOrderCounts] = useState<OrderCounts>({ pending: 0, completed: 0, cancelled: 0 });
  const [currentPage, setCurrentPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const itemsPerPage = 10;

  useEffect(() => {
    const loadData = async () => {
      if (!vendorId) return;
      
      try {
        setLoading(true);
        const [profileResponse, ordersResponse] = await Promise.all([
          fetchProductPartnerProfile(vendorId),
          fetchVendorOrders(vendorId)
        ]);
        setProductPartnerData(profileResponse.data);
        setServiceStatus(profileResponse.data.vendor.isActive);
        setOrders(ordersResponse.orders);
        setFilteredOrders(ordersResponse.orders);
        setOrderCounts(ordersResponse.orderCounts);
      } catch (err) {
        console.error('Error loading data:', err);
        setError('Failed to load data. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [vendorId]);

  const handleToggleStatus = async (vendorId: string, currentStatus: string) => {
    try {
      setUpdatingStatus(vendorId);
      const response = await toggleVendorStatus(vendorId);
      
      if (response.success) {
        setProductPartnerData((prev: ProductPartner | null) => {
          if (!prev) return null;
          return {
            ...prev,
            vendor: {
              ...prev.vendor,
              status: response.data.status as 'approved' | 'pending' | 'rejected'
            }
          };
        });
      } else {
        setError('Failed to update vendor status. Please try again.');
      }
    } catch (err) {
      console.error('Error toggling status:', err);
      setError('Failed to toggle status. Please try again later.');
    } finally {
      setUpdatingStatus(null);
    }
  };

  const handleSearch = (query: string) => {
    setSearchQuery(query);
    filterOrders(query, statusFilter);
  };

  const handleFilter = (status: string) => {
    setStatusFilter(status);
    filterOrders(searchQuery, status);
  };

  const filterOrders = (query: string, status: string) => {
    let filtered = orders;

    // Apply search filter
    if (query) {
      filtered = filtered.filter(order => 
        order.orderId.toLowerCase().includes(query.toLowerCase()) ||
        order.userDetails.name.toLowerCase().includes(query.toLowerCase()) ||
        order.userDetails.emailId.toLowerCase().includes(query.toLowerCase()) ||
        order.items.some(item => item.productDetails.name.toLowerCase().includes(query.toLowerCase()))
      );
    }

    // Apply status filter
    if (status !== 'all') {
      filtered = filtered.filter(order => order.status === status);
    }

    setFilteredOrders(filtered);
    setCurrentPage(1);
  };

  const totalPages = Math.ceil(filteredOrders.length / itemsPerPage);
  const paginatedOrders = filteredOrders.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  // Calculate financial metrics
  const totalRevenue = orders.reduce((sum, order) => sum + order.totalAmount, 0);
  const gstCollected = totalRevenue * 0.18; // Assuming 18% GST
  const totalDeliveredOrders = orders.filter(order => order.status === 'delivered').length;
  const totalPendingOrders = orders.filter(order => order.status === 'pending' || order.status === 'out_for_delivery').length;

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error || !productPartnerData) {
    return (
      <div className="p-6 text-center">
        <p className="text-red-600 dark:text-red-400">{error || 'Product partner data not found'}</p>
      </div>
    );
  }

  return (
    <div className="w-full min-w-0 max-w-7xl mx-auto p-3 sm:p-4 md:p-6">
      {/* Header with Vendor Info */}
      <div className="mb-8 bg-white dark:bg-gray-800 rounded-xl shadow-sm p-6 border border-gray-100 dark:border-gray-700">
        <div className="flex flex-col space-y-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <div className="p-3 bg-blue-50 dark:bg-blue-900/30 rounded-lg">
              <ShoppingBagIcon className="h-8 w-8 text-blue-600 dark:text-blue-400" />
            </div>
            <div>
                <h1 className="text-2xl font-bold text-gray-900 dark:text-white">{productPartnerData.brandName}</h1>
              <div className="flex items-center space-x-4 mt-2">
                <div className="flex items-center text-gray-600 dark:text-gray-400">
                  <MapPinIcon className="h-4 w-4 mr-1" />
                    <span className="text-sm">{productPartnerData.address}, {productPartnerData.city}, {productPartnerData.state}</span>
                </div>
                <div className="flex items-center text-gray-600 dark:text-gray-400">
                  <PhoneIcon className="h-4 w-4 mr-1" />
                    <span className="text-sm">{productPartnerData.phoneNumber}</span>
                </div>
                <div className="flex items-center text-gray-600 dark:text-gray-400">
                  <EnvelopeIcon className="h-4 w-4 mr-1" />
                    <span className="text-sm">{productPartnerData.email}</span>
                  </div>
                </div>
              </div>
            </div>
            <div className="flex items-center flex-wrap justify-end gap-2">
              <button
                onClick={() => setShowDetails(true)}
                className="p-2 text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/30 rounded-lg transition-colors"
                title="View Details"
              >
                <EyeIcon className="h-5 w-5" />
              </button>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-4 pt-4 border-t dark:border-gray-700">
            <div className="flex items-center space-x-2">
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Status:</span>
              <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                productPartnerData.vendor.status === 'approved'
                  ? 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300'
                  : productPartnerData.vendor.status === 'pending'
                  ? 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-300'
                  : 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300'
              }`}>
                {productPartnerData.vendor.status.charAt(0).toUpperCase() + productPartnerData.vendor.status.slice(1)}
                </span>
            </div>

            <div className="flex items-center space-x-2">
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Active:</span>
              <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                productPartnerData.vendor.isActive
                  ? 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300'
                  : 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300'
              }`}>
                {productPartnerData.vendor.isActive ? 'Active' : 'Inactive'}
              </span>
            </div>

            <div className="flex items-center space-x-2">
              <button
                onClick={() => handleToggleStatus(productPartnerData.vendor.vendorId, productPartnerData.vendor.status)}
                disabled={updatingStatus === productPartnerData.vendor.vendorId}
                className={`px-3 py-1 text-sm font-medium rounded-md transition-colors flex items-center space-x-2 ${
                  productPartnerData.vendor.status === 'approved'
                    ? 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-300 hover:bg-yellow-200 dark:hover:bg-yellow-900/50'
                    : 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300 hover:bg-green-200 dark:hover:bg-green-900/50'
                } disabled:opacity-50 disabled:cursor-not-allowed`}
              >
                {updatingStatus === productPartnerData.vendor.vendorId ? (
                  <>
                    <ArrowPathIcon className="w-4 h-4 animate-spin" />
                    <span>Updating...</span>
                  </>
                ) : (
                  <>
                    {productPartnerData.vendor.status === 'approved' ? (
                      <>
                        <ClockIcon className="w-4 h-4" />
                        <span>Mark as Pending</span>
                      </>
                    ) : (
                      <>
                        <CheckCircleIcon className="w-4 h-4" />
                        <span>Mark as Approve</span>
                      </>
                    )}
                  </>
                )}
              </button>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-2 pt-3">
            <button
              type="button"
              onClick={() => navigate(`/vendors/product/${vendorId}/event-timeline`)}
              className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
            >
              <RectangleStackIcon className="h-5 w-5 text-teal-600 dark:text-teal-400" />
              Event timeline
            </button>
            <button
              type="button"
              onClick={() => navigate(`/vendors/product/${vendorId}/login-activity`)}
              className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
            >
              <ShieldCheckIcon className="h-5 w-5 text-indigo-600 dark:text-indigo-400" />
              Login activity
            </button>
          </div>
        </div>
      </div>

      {/* Details Modal */}
      {showDetails && productPartnerData && (
        <div className="fixed inset-0 z-50 flex items-end justify-center overflow-y-auto bg-black/50 p-2 pb-[max(0.5rem,env(safe-area-inset-bottom))] sm:items-center sm:p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-4xl w-full max-h-[85vh] overflow-hidden">
            <div className="flex justify-between items-center border-b dark:border-gray-700 p-4">
              <div className="flex items-center space-x-3">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                  Product Partner Details
                </h3>
                <span className={`px-2 py-1 text-xs font-semibold rounded-full ${
                  productPartnerData.vendor.status === 'approved'
                    ? 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300'
                    : productPartnerData.vendor.status === 'pending'
                    ? 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-300'
                    : 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300'
                }`}>
                  {productPartnerData.vendor.status.charAt(0).toUpperCase() + productPartnerData.vendor.status.slice(1)}
                </span>
              </div>
            <button
                onClick={() => setShowDetails(false)}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 focus:outline-none"
            >
                <XCircleIcon className="w-5 h-5" />
            </button>
            </div>
            
            <div className="p-6 overflow-y-auto max-h-[calc(85vh-4rem)]">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Basic Information */}
                <div className="space-y-4">
                  <h4 className="text-sm font-semibold text-gray-900 dark:text-white uppercase tracking-wider">Basic Information</h4>
                  <div className="space-y-3">
                    <div>
                      <label className="text-sm text-gray-500 dark:text-gray-400">Company Legal Name</label>
                      <p className="mt-1 text-gray-900 dark:text-white">{productPartnerData.companyLegalName}</p>
                    </div>
                    <div>
                      <label className="text-sm text-gray-500 dark:text-gray-400">Brand Name</label>
                      <p className="mt-1 text-gray-900 dark:text-white">{productPartnerData.brandName}</p>
                    </div>
                    <div>
                      <label className="text-sm text-gray-500 dark:text-gray-400">GST Number</label>
                      <p className="mt-1 text-gray-900 dark:text-white">{productPartnerData.gstNumber}</p>
                    </div>
                    <div>
                      <label className="text-sm text-gray-500 dark:text-gray-400">PAN Number</label>
                      <p className="mt-1 text-gray-900 dark:text-white">{productPartnerData.panCardNumber}</p>
                    </div>
                  </div>
                </div>

                {/* Contact Information */}
                <div className="space-y-4">
                  <h4 className="text-sm font-semibold text-gray-900 dark:text-white uppercase tracking-wider">Contact Information</h4>
                  <div className="space-y-3">
                    <div>
                      <label className="text-sm text-gray-500 dark:text-gray-400">Phone Number</label>
                      <p className="mt-1 text-gray-900 dark:text-white">{productPartnerData.phoneNumber}</p>
                    </div>
                    <div>
                      <label className="text-sm text-gray-500 dark:text-gray-400">Email</label>
                      <p className="mt-1 text-gray-900 dark:text-white">{productPartnerData.email}</p>
                    </div>
                    <div>
                      <label className="text-sm text-gray-500 dark:text-gray-400">Bank Account</label>
                      <p className="mt-1 text-gray-900 dark:text-white">{productPartnerData.bankAccountNumber}</p>
                    </div>
                  </div>
                </div>

                {/* Location Information */}
                <div className="space-y-4">
                  <h4 className="text-sm font-semibold text-gray-900 dark:text-white uppercase tracking-wider">Location Information</h4>
                  <div className="space-y-3">
                    <div>
                      <label className="text-sm text-gray-500 dark:text-gray-400">Address</label>
                      <p className="mt-1 text-gray-900 dark:text-white">{productPartnerData.address}</p>
                    </div>
                    <div>
                      <label className="text-sm text-gray-500 dark:text-gray-400">Location</label>
                      <p className="mt-1 text-gray-900 dark:text-white">{productPartnerData.city}, {productPartnerData.state} - {productPartnerData.pincode}</p>
                    </div>
                  </div>
                </div>

                {/* License Details */}
                <div className="space-y-4">
                  <h4 className="text-sm font-semibold text-gray-900 dark:text-white uppercase tracking-wider">License Details</h4>
                  <div className="space-y-3">
                    {productPartnerData.licenseDetails.map((license, index) => (
                      <div key={index} className="p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                        <div className="flex justify-between items-start">
                          <div>
                            <p className="text-sm font-medium text-gray-900 dark:text-white">{license.name}</p>
                            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                              Number: {license.number}
                            </p>
                            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                              Expires: {new Date(license.expiry).toLocaleDateString()}
                            </p>
                          </div>
                          <div className="mt-2">
                            <FilePreview 
                              url={license.filePath}
                              name={license.name}
                              className="w-full"
                            />
                          </div>
                        </div>
                      </div>
          ))}
        </div>
      </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Core Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <MetricCard
          title="Total Orders"
          value={orders.length}
          icon={<ShoppingBagIcon className="h-6 w-6 text-blue-600 dark:text-blue-400" />}
          trend="up"
          trendValue={`${orderCounts.completed} completed`}
          description="Total orders received"
        />
        <MetricCard
          title="Units Sold"
          value={orders.reduce((sum, order) => sum + order.items.reduce((itemSum, item) => itemSum + item.quantity, 0), 0)}
          icon={<ShoppingCartIcon className="h-6 w-6 text-green-600 dark:text-green-400" />}
          trend="up"
          trendValue={`${totalDeliveredOrders} delivered`}
          description="Total units sold"
        />
        <MetricCard
          title="Pending Orders"
          value={totalPendingOrders}
          icon={<ClockIcon className="h-6 w-6 text-yellow-600 dark:text-yellow-400" />}
          trend="down"
          trendValue={`${orderCounts.pending} in queue`}
          description="Orders to be processed"
        />
        <MetricCard
          value={`₹${totalRevenue.toLocaleString()}`}
          title="Total Revenue"
          icon={<CurrencyDollarIcon className="h-6 w-6 text-blue-600 dark:text-blue-400" />}
          trend="up"
          trendValue={`${orderCounts.completed} completed orders`}
          description="Revenue generated"
        />
      </div>

      {/* Main Content Grid */}
      <div className="space-y-6">
        {/* Orders List - Full Width */}
        <OrderList
          orders={paginatedOrders}
          onSearch={handleSearch}
          onFilter={handleFilter}
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={setCurrentPage}
        />

        {/* Two Column Layout for Other Sections */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Column */}
        <div className="lg:col-span-2 space-y-6">
          {/* Product Performance */}
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-6 border border-gray-100 dark:border-gray-700">
            <h2 className="text-xl font-semibold mb-4 text-gray-900 dark:text-white">Product Performance</h2>
            <div className="space-y-4">
              {[
                {
                  name: 'Organic Face Wash',
                  views: 1250,
                  cartAdds: 450,
                  conversions: 280,
                  revenue: 84000
                },
                {
                  name: 'Ayurvedic Hair Oil',
                  views: 980,
                  cartAdds: 320,
                  conversions: 210,
                  revenue: 63000
                },
                {
                  name: 'Herbal Tea Blend',
                  views: 850,
                  cartAdds: 280,
                  conversions: 190,
                  revenue: 57000
                }
              ].map((product) => (
                <div key={product.name} className="p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                  <div className="flex justify-between items-start mb-2">
                    <div>
                      <p className="text-sm font-medium text-gray-900 dark:text-white">{product.name}</p>
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        Revenue: ₹{product.revenue.toLocaleString()}
                      </p>
                    </div>
                  </div>
                  <div className="grid grid-cols-3 gap-2 mt-2">
                    <div className="text-center">
                      <p className="text-sm text-gray-600 dark:text-gray-400">Views</p>
                      <p className="text-sm font-medium text-gray-900 dark:text-white">{product.views}</p>
                    </div>
                    <div className="text-center">
                      <p className="text-sm text-gray-600 dark:text-gray-400">Cart Adds</p>
                      <p className="text-sm font-medium text-gray-900 dark:text-white">{product.cartAdds}</p>
                    </div>
                    <div className="text-center">
                      <p className="text-sm text-gray-600 dark:text-gray-400">Conversions</p>
                      <p className="text-sm font-medium text-gray-900 dark:text-white">{product.conversions}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Recent Returns */}
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-6 border border-gray-100 dark:border-gray-700">
            <h2 className="text-xl font-semibold mb-4 text-gray-900 dark:text-white">Recent Returns</h2>
            <div className="space-y-4">
              {[
                {
                  product: 'Organic Face Wash',
                  reason: 'Product damaged',
                  comment: 'Package was damaged during delivery',
                  date: '2024-03-15'
                },
                {
                  product: 'Ayurvedic Hair Oil',
                  reason: 'Wrong product',
                  comment: 'Received different variant than ordered',
                  date: '2024-03-14'
                }
              ].map((return_) => (
                <div key={return_.product} className="p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                  <div className="flex justify-between items-start">
                    <div>
                      <p className="text-sm font-medium text-gray-900 dark:text-white">{return_.product}</p>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                        Reason: {return_.reason}
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                        {return_.comment}
                      </p>
                    </div>
                    <span className="text-xs text-gray-500 dark:text-gray-400">
                      {new Date(return_.date).toLocaleDateString()}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Customer Demographics */}
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-6 border border-gray-100 dark:border-gray-700">
            <h2 className="text-xl font-semibold mb-4 text-gray-900 dark:text-white">Customer Demographics</h2>
            <div className="grid grid-cols-2 gap-6">
              <div>
                <h3 className="text-sm font-medium text-gray-900 dark:text-white mb-3">Age Distribution</h3>
                <div className="space-y-3">
                  {[
                    { range: '18-24', percentage: 25 },
                    { range: '25-34', percentage: 40 },
                    { range: '35-44', percentage: 20 },
                    { range: '45-54', percentage: 10 },
                    { range: '55+', percentage: 5 },
                  ].map((item) => (
                    <div key={item.range} className="flex items-center justify-between">
                      <span className="text-sm text-gray-600 dark:text-gray-400">{item.range}</span>
                      <div className="flex items-center">
                        <div className="w-32 h-2 bg-gray-200 dark:bg-gray-600 rounded-full mr-2">
                          <div 
                            className="h-2 bg-blue-500 rounded-full"
                            style={{ width: `${item.percentage}%` }}
                          />
                        </div>
                        <span className="text-sm font-medium text-gray-900 dark:text-white">{item.percentage}%</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              <div>
                <h3 className="text-sm font-medium text-gray-900 dark:text-white mb-3">Top Locations</h3>
                <div className="space-y-3">
                  {[
                    { location: 'Mumbai', percentage: 35 },
                    { location: 'Delhi', percentage: 25 },
                    { location: 'Bangalore', percentage: 20 },
                    { location: 'Chennai', percentage: 15 },
                    { location: 'Others', percentage: 5 },
                  ].map((item) => (
                    <div key={item.location} className="flex items-center justify-between">
                      <span className="text-sm text-gray-600 dark:text-gray-400">{item.location}</span>
                      <div className="flex items-center">
                        <div className="w-32 h-2 bg-gray-200 dark:bg-gray-600 rounded-full mr-2">
                          <div 
                            className="h-2 bg-blue-500 rounded-full"
                            style={{ width: `${item.percentage}%` }}
                          />
                        </div>
                        <span className="text-sm font-medium text-gray-900 dark:text-white">{item.percentage}%</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Right Column */}
        <div className="space-y-6">
          {/* Financial Overview */}
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-6 border border-gray-100 dark:border-gray-700">
            <h2 className="text-xl font-semibold mb-4 text-gray-900 dark:text-white">Financial Overview</h2>
            <div className="space-y-4">
              <div className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                <div className="flex items-center">
                  <CurrencyDollarIcon className="h-5 w-5 text-green-500 dark:text-green-400 mr-2" />
                  <span className="text-sm text-gray-600 dark:text-gray-400">Total Revenue</span>
                </div>
                  <span className="text-sm font-medium text-gray-900 dark:text-white">₹{totalRevenue.toLocaleString()}</span>
              </div>
              <div className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                <div className="flex items-center">
                  <CalculatorIcon className="h-5 w-5 text-blue-500 dark:text-blue-400 mr-2" />
                  <span className="text-sm text-gray-600 dark:text-gray-400">GST Collected</span>
                </div>
                  <span className="text-sm font-medium text-gray-900 dark:text-white">₹{gstCollected.toLocaleString()}</span>
              </div>
              <div className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                <div className="flex items-center">
                  <TagIcon className="h-5 w-5 text-yellow-500 dark:text-yellow-400 mr-2" />
                    <span className="text-sm text-gray-600 dark:text-gray-400">Net Revenue</span>
                </div>
                  <span className="text-sm font-medium text-gray-900 dark:text-white">₹{(totalRevenue - gstCollected).toLocaleString()}</span>
              </div>
              <div className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                <div className="flex items-center">
                  <ArrowUturnLeftIcon className="h-5 w-5 text-red-500 dark:text-red-400 mr-2" />
                    <span className="text-sm text-gray-600 dark:text-gray-400">Average Order Value</span>
                </div>
                  <span className="text-sm font-medium text-gray-900 dark:text-white">₹{(totalRevenue / orders.length || 0).toLocaleString()}</span>
              </div>
            </div>
          </div>

          {/* Stock Alerts */}
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-6 border border-gray-100 dark:border-gray-700">
            <h2 className="text-xl font-semibold mb-4 text-gray-900 dark:text-white">Stock Alerts</h2>
            <div className="space-y-4">
              {[
                { product: 'Organic Face Wash', currentStock: 15, threshold: 20, category: 'Skincare' },
                { product: 'Ayurvedic Hair Oil', currentStock: 8, threshold: 25, category: 'Haircare' },
                { product: 'Herbal Tea Blend', currentStock: 12, threshold: 30, category: 'Wellness' }
              ].map((alert) => (
                <div key={alert.product} className="p-3 bg-red-50 dark:bg-red-900/30 rounded-lg">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-red-800 dark:text-red-300">{alert.product}</p>
                      <p className="text-xs text-red-600 dark:text-red-400 mt-1">
                        Current Stock: {alert.currentStock} units
                      </p>
                    </div>
                    <div className="flex items-center">
                      <ExclamationTriangleIcon className="h-5 w-5 text-red-500 dark:text-red-400 mr-2" />
                      <span className="text-sm font-medium text-red-800 dark:text-red-300">Low Stock</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Delivery Metrics */}
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-6 border border-gray-100 dark:border-gray-700">
            <h2 className="text-xl font-semibold mb-4 text-gray-900 dark:text-white">Delivery Metrics</h2>
            <div className="space-y-4">
              <div className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                <div className="flex items-center">
                  <ClockIcon className="h-5 w-5 text-blue-500 dark:text-blue-400 mr-2" />
                  <span className="text-sm text-gray-600 dark:text-gray-400">Average Delivery Time</span>
                </div>
                <span className="text-sm font-medium text-gray-900 dark:text-white">2.5 days</span>
              </div>
              <div className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                <div className="flex items-center">
                  <CheckCircleIcon className="h-5 w-5 text-green-500 dark:text-green-400 mr-2" />
                  <span className="text-sm text-gray-600 dark:text-gray-400">Successful Deliveries</span>
                </div>
                <span className="text-sm font-medium text-gray-900 dark:text-white">98.5%</span>
              </div>
              <div className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                <div className="flex items-center">
                  <XCircleIcon className="h-5 w-5 text-red-500 dark:text-red-400 mr-2" />
                  <span className="text-sm text-gray-600 dark:text-gray-400">Failed Deliveries</span>
                </div>
                <span className="text-sm font-medium text-gray-900 dark:text-white">1.5%</span>
              </div>
            </div>
          </div>

          {/* System Reminders */}
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-6 border border-gray-100 dark:border-gray-700">
            <h2 className="text-xl font-semibold mb-4 text-gray-900 dark:text-white">System Reminders</h2>
            <div className="space-y-4">
              <div className="p-4 bg-yellow-50 dark:bg-yellow-900/30 rounded-lg">
                <div className="flex items-start">
                  <BellAlertIcon className="h-5 w-5 text-yellow-500 dark:text-yellow-400 mr-2 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-yellow-800 dark:text-yellow-300">
                      Update Required
                    </p>
                    <p className="text-xs text-yellow-700 dark:text-yellow-400 mt-1">
                      Please update your product listings and prices for Q2 2024
                    </p>
                    <p className="text-xs text-yellow-600 dark:text-yellow-500 mt-2">
                      Due by: March 31, 2024
                    </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductVendorDashboard; 